/**
 * Phase 20 — the editor's auto-send slice, pure: seeded from the row's layer
 * (a master config's = its campaign group's; a container's = its own),
 * turned into the PUT body on Save (only what changed; a container sends its
 * limits only while it keeps its own layer), what blocks Save, and whether
 * this Save switches a group ON (real SMS — asked first).
 * For Media Devoted | Created By Ciro Autiero
 */
import { DROP_GEN_AUTOSEND_DEFAULTS, type DropGenAutoSendConfig } from '@trafficpulse/shared';

import type { ContainerDraft, DropGenPlanDetail } from '../../../drop-generator.types';
import { draftProblems, draftToPatch, layerToDraft } from '../../board-header/autosend/settings/autosend-draft';
import { flatRuleToTree } from '../state/satellites/verdict-draft';
import type { AutoSendPatch } from '../../board-header/autosend/use-autosend-mutations';

export const autoSendSeed = (config: DropGenAutoSendConfig | null | undefined) => {
  const base = config ?? DROP_GEN_AUTOSEND_DEFAULTS;
  return { autoSend: layerToDraft(base), autoSendBase: base };
};

/** A container keeps its own layer while this is on (its limits apply). */
export const ownsAutoSend = (draft: ContainerDraft) => draft.kind !== 'MASTER' && draft.ownSections.autoSend === true;

/** The PUT containers/:id/autosend body this Save sends — {} = none. */
export function autoSendSavePatch(draft: ContainerDraft, persistedOwn: boolean): AutoSendPatch {
  if (draft.kind === 'MASTER') return draftToPatch(draft.autoSendBase, draft.autoSend);
  const own = ownsAutoSend(draft);
  if (!own) return persistedOwn ? { own: false } : {};
  const limits = draftToPatch(draft.autoSendBase, draft.autoSend);
  return persistedOwn ? limits : { own: true, ...limits };
}

/** Why Save is off because of auto-send — null = it may save. */
export function autoSendProblem(draft: ContainerDraft): string | null {
  if (draft.kind === 'MASTER') return draftProblems(draft.autoSend)[0] ?? null;
  return ownsAutoSend(draft) ? (draftProblems(draft.autoSend)[0] ?? null) : null;
}

/** This Save makes a campaign group Live where it was not — the confirm
 *  comes first. Simulated sends nothing: no ask. */
export const switchesGroupOn = (draft: ContainerDraft) =>
  draft.kind === 'MASTER' &&
  draft.autoSend.enabled &&
  !draft.autoSend.shadow &&
  (!(draft.autoSendBase.autoSend && !draft.autoSendBase.shadow) ||
    (draft.autoSend.dispatchMode === 'SCHEDULE_AHEAD' && draft.autoSendBase.dispatchMode !== 'SCHEDULE_AHEAD'));

/** The layer as it will be after Save — what the confirm and "Right now" read. */
export function draftConfig(draft: ContainerDraft): DropGenAutoSendConfig {
  const d = draft.autoSend;
  const base = draft.autoSendBase;
  return {
    ...base,
    autoSend: d.enabled,
    dispatchMode: d.dispatchMode ?? 'DUE_TIME',
    shadow: d.shadow,
    dailySmsCap: d.noCap ? null : d.cap,
    stopLossJson: d.advanced ? base.stopLossJson : d.rows.length === 0 ? null : (flatRuleToTree(d) as Record<string, unknown>),
    stopLossEnabled: d.stopLossEnabled,
    stopLossMinSampleSent: d.minSample,
    stopLossDelayMin: d.delayMin,
  };
}

/** Would this row's drafts go by themselves, as edited? A master config: its
 *  switch. A container: its group's switch, and its own layer's if it has one. */
export function sendsByItself(draft: ContainerDraft, plan: Pick<DropGenPlanDetail, 'masters'>): boolean {
  if (draft.kind === 'MASTER') return draft.autoSend.enabled;
  const groupOn = (plan.masters ?? []).find((m) => m.id === draft.masterId)?.autoSend.autoSend === true;
  return groupOn && (!ownsAutoSend(draft) || draft.autoSend.enabled);
}
